import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { bool, func, string } from 'prop-types';

import { SUBSCRIPTION_STATUSES } from '@lootboy/lib/constants';

import IOSSwitch from '~/components/IOSSwitch';
import CancelSubscriptionPopup from '~/components/SubscriptionOverview/NewView/PopupsSection/CancelSubscriptionPopup';
import DiscordPopup from '~/components/SubscriptionOverview/NewView/PopupsSection/DiscordPopup';
import SectionInfo from '~/components/SubscriptionOverview/NewView/SectionInfo';
import Content from '~/components/SubscriptionOverview/NewView/SectionInfo/Content';
import Title from '~/components/SubscriptionOverview/NewView/SectionInfo/Title';
import SectionTitle from '~/components/SubscriptionOverview/NewView/SectionTitle';
import SubscriptionPopup from '~/components/SubscriptionPopup';

import useBooleanState from '~/hooks/useBooleanState';
import { SEASON_TICKET_PATH } from '~/routes/paths';

import styles from './PopupsSection.module.scss';

export default function PopupsSection({
  discordUrl,
  isDiscordConnected,
  showSuccessDialog,
  subscriptionStatus,
  subscriptionValidUntil,
  toggleShowSuccessDialog,
}) {
  const history = useHistory();

  const [showDiscord, toggleShowDiscord] = useBooleanState();
  const [showCancelSub, toggleShowCancelSub] = useBooleanState();

  const isSubscriptionCanceled =
    subscriptionStatus === SUBSCRIPTION_STATUSES.CANCELED;

  const goToSeasonPage = useCallback(
    () => history.push(SEASON_TICKET_PATH),
    [history],
  );

  return (
    <>
      <SectionTitle title={I18n.t('subscription_summary_premium_set')} />
      <SectionInfo
        title={<Title text={I18n.t('season_ticket_perks_likes_headline')} />}
        content={
          <IOSSwitch
            checked
            disabled
          />
        }
      />
      <SectionInfo
        title={<Title text={I18n.t('season_ticket_perks_ads_headline')} />}
        content={
          <IOSSwitch
            checked
            disabled
          />
        }
      />
      <SectionInfo
        title={
          <Title text={I18n.t('season_ticket_perks_discord_rank_headline')} />
        }
        content={
          <IOSSwitch
            checked={isDiscordConnected}
            disabled={isDiscordConnected}
            onChange={() => toggleShowDiscord()}
          />
        }
      />
      <SectionTitle title={I18n.t('subscription_summary_details')} />
      {isSubscriptionCanceled && (
        <SectionInfo
          content={
            <Content
              text={subscriptionValidUntil}
              className={styles.redText}
            />
          }
          title={
            <Title
              textClassName={classNames(styles.text, styles.redText)}
              text={I18n.t('season_ticket_popup_cancelled')}
            />
          }
        />
      )}
      <SectionInfo
        title={<Title text={I18n.t('subscription_summary_charge')} />}
        content={<Content text={subscriptionValidUntil} />}
      />
      <SectionInfo
        title={
          <Title
            hrefClassName={classNames(styles.text, styles.seasonPageHref)}
            onClick={goToSeasonPage}
            text={I18n.t('subscription_summary_to_season_page')}
          />
        }
      />
      {!isSubscriptionCanceled && (
        <SectionInfo
          title={
            <Title
              hrefClassName={classNames(
                styles.text,
                styles.redText,
                styles.seasonPageCancelSub,
              )}
              onClick={toggleShowCancelSub}
              text={I18n.t('season_ticket_popup_cancel_sbutton')}
            />
          }
        />
      )}
      <DiscordPopup
        discordUrl={discordUrl}
        show={showDiscord}
        toggleShow={toggleShowDiscord}
      />
      <CancelSubscriptionPopup
        show={showCancelSub}
        subscriptionValidUntil={subscriptionValidUntil}
        toggleShow={toggleShowCancelSub}
      />
      <SubscriptionPopup
        show={showSuccessDialog}
        onClose={toggleShowSuccessDialog}
        description="season_ticket_popup_confirmation_description"
        onClick={toggleShowSuccessDialog}
        buttonText="season_ticket_popup_confirmation_button"
        header="season_ticket_popup_confirmation_headline"
      />
    </>
  );
}

PopupsSection.propTypes = {
  discordUrl: string,
  isDiscordConnected: bool,
  showSuccessDialog: bool,
  subscriptionStatus: string,
  subscriptionValidUntil: string,
  toggleShowSuccessDialog: func,
};
